import { useCallback, useEffect, useMemo, useState } from "react";
import type { GroupResponse } from "@/app/api/types/group";
import { canEditGroupContextRole, isGroupAdminRole } from "@/lib/group-admin";

export interface GroupContextEntry {
  name: string;
  ai_context: string | null;
  /**
   * Group AI context is editable by the group's owner, its admins (ADMIN or
   * ADMIN_AND_HR), and CONTEXT_EDITOR members — the set the V2
   * `PATCH /api/v2/groups/{id}` auth matrix admits for `aiContext` (see
   * `canEditGroupContextRole`). Plain members (and non-members) can read a
   * group's context but never edit it.
   */
  canEdit: boolean;
  /**
   * Whether the caller administers the group (owner, ADMIN or ADMIN_AND_HR) —
   * the set the V2 workflow-instruction auth admits for group-scope rows.
   * Strictly narrower than {@link canEdit}: a CONTEXT_EDITOR may edit the
   * group's `aiContext` and nothing else, so any surface writing another
   * group-scoped resource must gate on this instead.
   */
  canAdminister: boolean;
}

export interface GroupContextEditor {
  /** Per-group lookup of display name, stored context, and edit permission. */
  groupsById: Map<string, GroupContextEntry>;
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
  /** In-flight edits keyed by group id (absent key ⇒ untouched). */
  groupEdits: Map<string, string>;
  setGroupEdit: (groupId: string, value: string) => void;
  resetGroupEdits: () => void;
  /** Whether the caller may edit the currently-selected group's context. */
  canEditSelectedGroup: boolean;
  /** Whether the caller administers the currently-selected group. */
  canAdministerSelectedGroup: boolean;
  /** Effective textarea value for the selected group (edit or stored). */
  currentGroupValue: string;
  /** True when any edit differs from its stored value. */
  groupContextDirty: boolean;
  /**
   * True only when the *selected* group is editable and its edit differs from
   * the stored value. Surfaces drive their save/discard affordances off this
   * so a stale edit on a different (e.g. read-only) group can never strand a
   * dirty indicator with no reachable way to resolve it.
   */
  selectedGroupDirty: boolean;
  /** Changed edits the caller is allowed to persist (skips read-only groups). */
  collectDirtyEditableEdits: () => Array<[string, string]>;
}

/**
 * Shared state + derivations for the group AI-context editors used by both the
 * full AI management panel and the general-context dialog. Centralizing it
 * keeps the owner/admin permission rule and dirty-tracking in one place.
 */
export function useGroupContextEditor(
  myGroups: GroupResponse[] | undefined,
  userId: string,
  options?: { active?: boolean },
): GroupContextEditor {
  const active = options?.active ?? true;

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupEdits, setGroupEdits] = useState<Map<string, string>>(new Map());

  const groupsById = useMemo(() => {
    const map = new Map<string, GroupContextEntry>();
    for (const g of myGroups ?? []) {
      const isOwner = g.owner_id === userId;
      const hasEditingRole = g.members.some(
        (m) => m.user_id === userId && canEditGroupContextRole(m.role),
      );
      const isAdmin = g.members.some(
        (m) => m.user_id === userId && isGroupAdminRole(m.role),
      );
      map.set(g.id, {
        name: g.name,
        ai_context: g.ai_context ?? null,
        canEdit: isOwner || hasEditingRole,
        canAdminister: isOwner || isAdmin,
      });
    }
    return map;
  }, [myGroups, userId]);

  const canEditSelectedGroup = selectedGroupId
    ? (groupsById.get(selectedGroupId)?.canEdit ?? false)
    : false;

  const canAdministerSelectedGroup = selectedGroupId
    ? (groupsById.get(selectedGroupId)?.canAdminister ?? false)
    : false;

  // Auto-select the first group when groups load, or reset the pick when it is
  // no longer in the list (e.g. user removed from the group in another tab).
  useEffect(() => {
    if (!active) return;
    if (!myGroups || myGroups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    setSelectedGroupId((cur) =>
      !cur || !groupsById.has(cur) ? myGroups[0].id : cur,
    );
  }, [active, myGroups, groupsById]);

  const currentGroupValue = useMemo(() => {
    if (!selectedGroupId) return "";
    if (groupEdits.has(selectedGroupId))
      return groupEdits.get(selectedGroupId) ?? "";
    return groupsById.get(selectedGroupId)?.ai_context ?? "";
  }, [selectedGroupId, groupEdits, groupsById]);

  const groupContextDirty = useMemo(() => {
    for (const [id, value] of groupEdits.entries()) {
      const stored = groupsById.get(id)?.ai_context ?? "";
      if ((value || "") !== stored) return true;
    }
    return false;
  }, [groupEdits, groupsById]);

  const selectedGroupDirty = useMemo(() => {
    if (!selectedGroupId) return false;
    const group = groupsById.get(selectedGroupId);
    if (!group?.canEdit || !groupEdits.has(selectedGroupId)) return false;
    const stored = group.ai_context ?? "";
    return (groupEdits.get(selectedGroupId) || "") !== stored;
  }, [selectedGroupId, groupEdits, groupsById]);

  const setGroupEdit = useCallback((groupId: string, value: string) => {
    setGroupEdits((cur) => {
      const next = new Map(cur);
      next.set(groupId, value);
      return next;
    });
  }, []);

  const resetGroupEdits = useCallback(() => setGroupEdits(new Map()), []);

  const collectDirtyEditableEdits = useCallback(() => {
    const dirty: Array<[string, string]> = [];
    for (const [groupId, value] of groupEdits.entries()) {
      const group = groupsById.get(groupId);
      if (!group?.canEdit) continue;
      const stored = group.ai_context ?? "";
      if ((value || "") === stored) continue;
      dirty.push([groupId, value]);
    }
    return dirty;
  }, [groupEdits, groupsById]);

  return {
    groupsById,
    selectedGroupId,
    setSelectedGroupId,
    groupEdits,
    setGroupEdit,
    resetGroupEdits,
    canEditSelectedGroup,
    canAdministerSelectedGroup,
    currentGroupValue,
    groupContextDirty,
    selectedGroupDirty,
    collectDirtyEditableEdits,
  };
}
